import React, { useState, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';

// Hook สำหรับตรวจสอบขนาดหน้าจอ
const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkIsMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    checkIsMobile();
    window.addEventListener('resize', checkIsMobile);

    return () => window.removeEventListener('resize', checkIsMobile);
  }, []);

  return isMobile;
};

// Static metadata (images, colors, links) — text comes from i18n
const courseCategories = {
  legal: [
    { titleKey: 'businessLaw', image: 'business-law.webp', color: '#FF9500', link: '/services/business-law' },
    { titleKey: 'taxLaw', image: 'tax-law.webp', color: '#007AFF', link: '/services/tax-law' },
    { titleKey: 'litigation', image: 'litigation.webp', color: '#007AFF', link: '/services/litigation' },
    { titleKey: 'otherLaw', image: 'other-law.webp', color: '#007AFF', link: '/services/other-law' },
    { titleKey: 'transferPricing', image: 'transfer-pricing.webp', color: '#007AFF', link: '/services/transfer-pricing' },
    { titleKey: 'testament', image: 'testament.webp', color: '#007AFF', link: '/services/testament' },
    { titleKey: 'civilLitigation', image: 'civil-litigation.webp', color: '#007AFF', link: '/services/civil-litigation' },
    { titleKey: 'divorce', image: 'divorce.webp', color: '#007AFF', link: '/services/divorce' },
  ],
  accounting: [
    { titleKey: 'accounting', image: 'accounting.webp', color: '#00D4FF', link: '/services/accounting' },
    { titleKey: 'payroll', image: 'payroll.webp', color: '#F7DF1E', link: '/services/payroll' },
  ],
  audit: [
    { titleKey: 'statementAudit', image: 'statement-audit.webp', color: '#FF3B30', link: '/services/statement-audit' },
    { titleKey: 'statementReview', image: 'statement-review.webp', color: '#31A8FF', link: '/services/statement-review' },
    { titleKey: 'dueDiligence', image: 'due-diligence.webp', color: '', link: '/services/due-diligence' },
    { titleKey: 'specialAudit', image: 'special-audit.webp', color: '', link: '/services/special-audit' },
    { titleKey: 'accountingAdvisory', image: 'accounting-advisory.webp', color: '', link: '/services/accounting-advisory' },
  ],
  internalAudit: [
    { titleKey: 'ia', image: 'internal-audit.webp', color: '#FF6B6B', link: '/services/internal-audit' },
    { titleKey: 'ipo', image: 'ipo.webp', color: '#4ECDC4', link: '/services/ipo' },
    { titleKey: 'cac', image: 'cac.webp', color: '#4ECDC4', link: '/services/cac' },
    { titleKey: 'qar', image: 'qar.webp', color: '#4ECDC4', link: '/services/qar' },
    { titleKey: 'itAudit', image: 'it-audit.webp', color: '#4ECDC4', link: '/services/it-audit' },
    { titleKey: 'systemDevelopment', image: 'system-development.webp', color: '#4ECDC4', link: '/services/system-development' },
    { titleKey: 'businessDevelopment', image: 'business-development.webp', color: '#4ECDC4', link: '/services/business-development' },
    { titleKey: 'specialInvestigation', image: 'special-audit.webp', color: '#4ECDC4', link: '/services/special-investigation' },
    { titleKey: 'esgReporting', image: 'esg-reporting.webp', color: '#4ECDC4', link: '/services/esg-reporting' },
    { titleKey: 'carbonFootprint', image: 'carbon-footprint.webp', color: '#4ECDC4', link: '/services/carbon-footprint' },
    { titleKey: 'dueDiligence', image: 'due-diligence.webp', color: '#4ECDC4', link: '/services/due-diligence' },
    { titleKey: 'inHouse', image: 'in-house.webp', color: '#4ECDC4', link: '/services/in-house' },
    { titleKey: 'aiGovernance', image: 'ai-governance.webp', color: '#4ECDC4', link: '/services/ai-governance' },
    { titleKey: 'aiGra', image: 'ai-gra.webp', color: '#4ECDC4', link: '/services/ai-gra' },
  ],
  websiteIt: [
    { titleKey: 'webService', image: 'web-service.webp', color: '#50E3C2', link: '/services/web-service' },
    { titleKey: 'webHosting', image: 'web-hosting.webp', color: '#B8E986', link: '/services/web-hosting' },
    { titleKey: 'webMaintenance', image: 'web-maintenance.webp', color: '#B8E986', link: '/services/web-maintenance' },
  ],
};

function OurServicesSection() {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState('legal');
  const [tabAnimating, setTabAnimating] = useState(false);
  const timeoutRef = useRef(null);
  const [visibleCards, setVisibleCards] = useState([]);
  const isMobile = useIsMobile();

  // Animation effect when tab changes
  useEffect(() => {
    if (tabAnimating) {
      timeoutRef.current = setTimeout(() => {
        setTabAnimating(false);
      }, 350);
    }
    return () => clearTimeout(timeoutRef.current);
  }, [tabAnimating]);

  // Staggered animation for cards
  useEffect(() => {
    if (!tabAnimating) {
      setVisibleCards([]);
      const cards = courseCategories[activeTab] || [];
      cards.forEach((_, idx) => {
        setTimeout(() => {
          setVisibleCards((prev) => {
            if (prev.includes(idx)) return prev;
            return [...prev, idx];
          });
        }, 100 * idx + 100);
      });
    }
    // eslint-disable-next-line
  }, [activeTab, tabAnimating]);

  const handleTabChange = (category) => {
    if (activeTab !== category) {
      setTabAnimating(true);
      setVisibleCards([]);
      setTimeout(() => {
        setActiveTab(category);
      }, 200);
    }
  };

  return (
    <div style={{ background: '#f8f8faff', padding: isMobile ? '80px 0' : '4vw 0', fontFamily: 'Prompt, sans-serif' }}>
      <div style={{ maxWidth: isMobile ? '1200px' : '85vw', margin: '0 auto', padding: isMobile ? '0 16px' : '0 1.5vw' }}>
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: isMobile ? '60px' : '3vw' }}>
          <h2 style={{
            fontSize: isMobile ? '3.5rem' : '2.8vw',
            fontWeight: 700,
            color: '#2d3a4b',
            marginBottom: isMobile ? '16px' : '1vw',
            letterSpacing: isMobile ? '-1px' : '-0.06vw'
          }}>
            {t('home.ourServices.sectionTitle')}
          </h2>
          <motion.div
              initial={{ width: 0 }}
              whileInView={{ width: "120px" }}
              transition={{ duration: 1, delay: 0.5 }}
              viewport={{ once: true }}
              className="mx-auto mb-4"
              style={{
                height: isMobile ? "4px" : "0.25vw",
                background: "#ff5d5dff",
                borderRadius: isMobile ? "2px" : "0.125vw",
              }}
            />
          <p style={{
            fontSize: isMobile ? '1.1rem' : '1.1vw',
            color: '#6c6f7dff',
            margin: 0
          }}>
            {t('home.ourServices.sectionDescription')}
          </p>
          <motion.div />
        </div>

        {/* Tab Navigation */}
        <div style={{
          background: '#ffdedeff',
          borderRadius: isMobile ? '16px' : '1vw',
          padding: isMobile ? '8px' : '0.5vw',
          marginBottom: isMobile ? '50px' : '2.5vw',
          display: 'flex',
          flexWrap: 'wrap',
          gap: isMobile ? '15px' : '0.8vw',
          justifyContent: 'center'
        }}>
          {Object.keys(courseCategories).map((categoryKey) => (
            <button
              key={categoryKey}
              onClick={() => handleTabChange(categoryKey)}
              style={{
                background: activeTab === categoryKey ? '#ff5959ff' : 'transparent',
                color: activeTab === categoryKey ? '#fff' : '#ba4242ff',
                border: 'none',
                borderRadius: isMobile ? '8px' : '0.5vw',
                padding: isMobile ? '12px 24px' : '0.7vw 1.4vw',
                fontSize: isMobile ? '1rem' : '1vw',
                fontWeight: 500,
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                outline: 'none'
              }}
            >
              {t(`serviceCategories.${categoryKey}.tab`)}
            </button>
          ))}
        </div>

        {/* Course Cards */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? 'repeat(auto-fit, minmax(280px, 1fr))' : 'repeat(auto-fit, minmax(15vw, 1fr))',
            gap: isMobile ? '25px' : '1.5vw',
            maxWidth: isMobile ? '900px' : '60vw',
            margin: '0 auto',
            opacity: tabAnimating ? 0 : 1,
            transform: tabAnimating ? (isMobile ? 'translateY(20px)' : 'translateY(1.2vw)') : 'translateY(0)',
            transition: 'opacity 0.35s cubic-bezier(.4,0,.2,1), transform 0.35s cubic-bezier(.4,0,.2,1)'
          }}
        >
          {(courseCategories[activeTab] || []).map((course, idx) => (
            <a
              key={idx}
              href={course.link}
              style={{
                textDecoration: 'none',
                color: 'inherit',
                display: 'block',
                height: '100%'
              }}
            >
              <div
                style={{
                  background: '#fff',
                  borderRadius: isMobile ? '16px' : '1vw',
                  overflow: 'hidden',
                  boxShadow: isMobile ? '0 4px 20px rgba(0,0,0,0.08)' : '0 0.25vw 1.2vw rgba(0,0,0,0.08)',
                  transition: 'all 0.5s cubic-bezier(.4,0,.2,1)',
                  cursor: 'pointer',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  opacity: visibleCards.includes(idx) ? 1 : 0,
                  transform: visibleCards.includes(idx)
                    ? 'translateY(0) scale(1)'
                    : (isMobile ? 'translateY(40px) scale(0.96)' : 'translateY(2.4vw) scale(0.96)'),
                  pointerEvents: visibleCards.includes(idx) ? 'auto' : 'none',
                  zIndex: visibleCards.includes(idx) ? 1 : 0
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = isMobile ? 'translateY(-8px) scale(1.03)' : 'translateY(-0.5vw) scale(1.03)';
                  e.currentTarget.style.boxShadow = isMobile ? '0 12px 40px rgba(0,0,0,0.15)' : '0 0.7vw 2.4vw rgba(0,0,0,0.15)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = visibleCards.includes(idx)
                    ? 'translateY(0) scale(1)'
                    : (isMobile ? 'translateY(40px) scale(0.96)' : 'translateY(2.4vw) scale(0.96)');
                  e.currentTarget.style.boxShadow = isMobile ? '0 4px 20px rgba(0,0,0,0.08)' : '0 0.25vw 1.2vw rgba(0,0,0,0.08)';
                }}
              >
                {course.image ? (
                  <div style={{
                    height: isMobile ? '200px' : '12vh',
                    background: course.image && !course.image.startsWith('http') && !course.image.startsWith('https')
                      ? `url(${require('../../image/services/' + course.image)}) center center/cover no-repeat`
                      : `url(${course.image}) center center/cover no-repeat`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    position: 'relative',
                    overflow: 'hidden'
                  }}>
                    <div style={{
                      position: 'absolute',
                      top: 0,
                      right: 0,
                      width: isMobile ? '100px' : '6vw',
                      height: isMobile ? '100px' : '6vw',
                      background: `radial-gradient(circle, ${course.color}30 0%, transparent 70%)`,
                      transform: isMobile ? 'translate(30px, -30px)' : 'translate(1.8vw, -1.8vw)'
                    }} />
                  </div>
                ) : (
                  <div style={{
                    height: isMobile ? '200px' : '12vh',
                    background: `linear-gradient(135deg, ${course.color}20 0%, ${course.color}40 100%)`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    position: 'relative',
                    overflow: 'hidden'
                  }}>
                    <div style={{
                      position: 'absolute',
                      top: 0,
                      right: 0,
                      width: isMobile ? '100px' : '6vw',
                      height: isMobile ? '100px' : '6vw',
                      background: `radial-gradient(circle, ${course.color}30 0%, transparent 70%)`,
                      transform: isMobile ? 'translate(30px, -30px)' : 'translate(1.8vw, -1.8vw)'
                    }} />
                  </div>
                )}
                <div style={{ padding: isMobile ? '24px' : '1.4vw' }}>
                  <h4 style={{
                    fontSize: isMobile ? '1.25rem' : '1vw',
                    fontWeight: 600,
                    color: '#2d3a4b',
                    marginBottom: isMobile ? '8px' : '0.5vw',
                    lineHeight: 1.3
                  }}>
                    {t(`serviceCategories.${activeTab}.items.${course.titleKey}`)}
                  </h4>
                  <p style={{
                    color: '#6c757d',
                    fontSize: isMobile ? '0.9rem' : '0.8vw',
                    lineHeight: 1.5,
                    marginBottom: isMobile ? '16px' : '1vw'
                  }}>
                    {t(`serviceCategories.${activeTab}.descriptions.${course.titleKey}`)}
                  </p>
                </div>
              </div>
            </a>
          ))}
        </div>
      </div>
    </div>
  );
}

export default OurServicesSection;
